// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/** Mirrors `zh-CN` key for key; a missing key renders as its raw path, so a test asserts
 * the two catalogues stay in step. */

export const enUS = {
  app: { title: "Cybertron Device Archive", subtitle: "Device Records System" },
  login: {
    sso: "Sign in with your company account",
    hint: "This system stores no passwords; sign-in is handled by company SSO.",
    retry: "Sign in again",
    errors: {
      DOMAIN_NOT_ALLOWED:
        "Only dynarobotics.ai company accounts can sign in. Choose your company account on Google's account picker.",
      EMAIL_NOT_VERIFIED:
        "This Google account's email address is not verified, so it cannot sign in.",
      ACCOUNT_DISABLED: "This account has been disabled. Please contact an administrator.",
      LOGIN_STATE_INVALID:
        "This sign-in attempt expired or was already used (for example, the page was refreshed). Please sign in again.",
      LOGIN_FAILED:
        "Sign-in did not complete (access may have been declined, or Google was unreachable). Please sign in again.",
      unknown: "Sign-in failed ({{code}}). Please sign in again.",
    },
  },
  nav: {
    archives: "Device List",
    productTypes: "Device Types",
    boms: "BOMs",
    workbench: "Create Device",
    archiveImport: "Import Devices",
    audit: "Audit Log",
    users: "Users",
    switchUser: "Switch user",
    signOut: "Sign out",
    accountMenu: "Account menu",
    system: "System",
    group: { work: "Records", masterData: "Master data", system: "System" },
  },
  roles: {
    ADMIN: "Administrator",
    ENGINEER: "Maintenance engineer",
    VISITOR: "Visitor",
    null: "",
    noAccess: "Your account ({{role}}) cannot use this page. Please contact an administrator.",
    readOnly: "Visitors can view but not change anything",
  },
  users: {
    title: "Users",
    hint: "An account appears here once it has signed in, as a maintenance engineer. Administrators manage device types, BOMs, the audit log and users; maintenance engineers file archives, record changes and scrap; visitors only read.",
    email: "Email",
    name: "Name",
    role: "Role",
    lastLogin: "Last sign-in",
    you: "(you)",
    roleOf: "Role of {{email}}",
  },
  common: {
    next: "Next",
    back: "Back",
    save: "Save",
    cancel: "Cancel",
    submit: "Submit",
    copied: "Copied",
    copyLink: "Copy link",
    copyFailed: "Copy failed: the browser does not allow clipboard access",
    create: "New",
    edit: "Edit",
    delete: "Delete",
    search: "Search",
    export: "Export CSV",
    loading: "Loading…",
    empty: "Nothing here yet",
    more: "Load more",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    refresh: "Refresh",
    close: "Close",
    retry: "Retry",
    required: "Required",
  },
  productType: {
    title: "Device Types",
    name: "Name",
    hasBom: "Uses a BOM",
    archivePrefix: "Archive prefix",
    archivePrefixHint:
      "2–8 upper-case letters or digits, starting with a letter (RBT, SM8B4). It leads every archive number this type will ever mint, and cannot be changed afterwards.",
    deviceNoRegex: "Device ID pattern",
    status: "Status",
    bomCount: "BOMs",
    archiveCount: "Records",
    discontinue: "Discontinue",
    activate: "Re-activate",
    discontinueHint:
      "Stops new records and disables every BOM of this type. Re-activating the type does not re-enable them.",
    createTitle: "New device type",
    immutableHint:
      "A device type cannot be edited after creation, only discontinued. Its archive prefix is baked into every archive number this type will ever mint.",
    deleteTitle: "Delete device type",
    deleteConfirm: "Delete “{{name}}”? This cannot be undone.",
    deleteBoms: "Its {{boms}} unused BOM(s) are deleted with it.",
    deleteBlocked:
      "“{{name}}” has {{archives}} record(s) and cannot be deleted. Discontinue it instead: that stops new records while every existing record keeps resolving.",
  },
  archiveImport: {
    moduleHint:
      "One label per module in the module label column, and the SN on its label in the Module SN column (required); a row per critical slot and lower module slot.",
    needsModuleType: "A module batch needs a product type, a BOM and a module type first",
    label: "Module label",
    downloadResult: "Download result",
    title: "Import Devices",
    defaultBom: "BOM version",
    bomFromFile: "BOM version: from the file",
    stepsLabel: "Batch filing steps",
    step1: "1 Choose type and BOM version",
    step2: "2 Download and fill the template",
    step3: "3 Upload — checked automatically",
    step4: "4 File once it passes",
    chooseFile: "Upload file",
    needsType:
      "Choose a device type before uploading. The template is the same for every type and can be downloaded now.",
    hint: "One row per serial, so a device spans several rows; the Robot SN column is required, with the SN on the device's label on every row. An upload is checked first; a batch is filed all or nothing.",
    reportTitle: "Preflight report",
    passed: "{{devices}} devices, {{serials}} serials. Ready to file.",
    failed:
      "{{count}} problems. All of them have to be fixed — a batch cannot file only the devices that are clean.",
    issueDetail: "Detail",
    commit: "File the batch",
    filedTitle: "Filed {{count}} devices",
  },
  barcodeRules: {
    title: "Barcode rules of material {{material}}",
    shared:
      "{{n}} positions in this BOM use this material ({{lines}}) and share these rules, as does the same material in other BOMs.",
    anyMatches:
      "A scanned serial is accepted when any one rule matches; with no rules, anything is. Rules can be added or changed after the BOM is published.",
    pattern: "Rule (regex)",
    example: "Example SN",
    examplePlaceholder: "Scan or type a real serial",
    none: "No rules yet: any serial is accepted",
    add: "Add a rule",
    addTitle: "Add a rule",
    editTitle: "Change the rule",
    edit: "Change rule {{pattern}}",
    delete: "Delete rule {{pattern}}",
    regenerate: "Regenerate from the example",
    generatedHint:
      "The rule is generated from the example: each run of digits takes as many digits, letters and symbols stay as they are. If a letter varies, edit the rule.",
    invalid: "The rule is not a valid regular expression",
    acceptsExample: "The rule matches the example SN",
    rejectsExample: "The rule does not match the example SN, so it cannot be saved",
    trial: "Try a serial",
    trialMatches: "Matches",
    trialFails: "Does not match",
  },
  bom: {
    title: "BOM Management",
    version: "Version",
    status: "Status",
    rows: "Lines",
    criticalItems: "Critical lines",
    archiveCount: "Records",
    disable: "Disable",
    enable: "Enable",
    disabledHint:
      "This BOM is disabled: it can no longer be used for new records or published. Existing records are unaffected; Enable restores it.",
    deleteBlocked:
      "{{count}} record(s) were created from this version, so it cannot be deleted; disable it instead.",
    deleteTitle: "Delete BOM",
    deleteDraftConfirm: "Delete draft {{version}} and its {{rows}} line(s)? This cannot be undone.",
    deleteConfirm: "Delete BOM {{version}} and all its lines? This cannot be undone.",
    requiredSn: "Serials required",
    publish: "Publish",
    publishTitle: "Publish BOM",
    unnumbered: "Not set",
    setVersion: "Set version",
    versionPlaceholder: "e.g. 1.5",
    versionHint:
      "1.0 to 9.99, at most two decimals; the version is the first three digits of the SN (1.5 → 150, 2.2 → 220). A number a product type has used cannot go to another BOM, even after deletion. Leave blank to clear.",
    publishConfirm:
      "Once published, nothing but the remarks can be changed; anything else needs a derived version. {{critical}} critical lines, expanding to {{slots}} serials to scan per device. The name may be left empty.",
    derive: "Derive new version",
    newDraft: "New draft",
    name: "Name",
    namePlaceholder: "e.g. Production",
    import: "Import a file",
    downloadTemplate: "Download template",
    importNeedsType:
      "Choose a device type before importing — the file is validated against it. The template can be downloaded at any time.",
    templateHint:
      "The template is an engineering BOM: the five shaded columns are required, hierarchy is parent part no. → part no., and the quantity is per machine. The second sheet carries the rules and the BOM sheet a worked example. Files in the former position-code format still import.",
    positionCode: "Position code",
    positionName: "Material Info",
    partName: "Part name",
    partNamePlaceholder: "Left blank: the parent line's part name",
    noModule: "None (not a module)",
    drawingNo: "Drawing no.",
    quantity: "Per parent",
    effectiveQuantity: "Per machine",
    critical: "Critical",
    barcodeRules: "Barcode rules",
    barcodeRulesOf: "Barcode rules of {{line}}",
    barcodeRuleCount: "{{n}} rule(s)",
    barcodeRulesAdd: "Add a rule",
    barcodeRulesNoMaterial: "No material code",
    importedBarcodeRules: "{{n}} barcode rule(s) will be generated from the SN examples",
    importedBarcodeRulesHint:
      "Rules are kept per material code and shared by its positions here and in other BOMs; an example the material's rules already match adds none. They can be changed on the BOM page after importing.",
    materialCode: "Material code",
    remark: "Remark",
    remarkIn: "Remark ({{language}})",
    remarkLanguage: "Remark language",
    remarkLanguages: { "zh-CN": "中文", "en-US": "English" },
    editRemark: "Edit the remark for {{line}}",
    expandSlots: "Show the {{n}} slots of {{line}}",
    findLine: "Search position code, name, material or remark",
    shownLines: "Showing {{n}} of {{total}} lines",
    noMatchingLines: "No lines match.",
    remarkHint:
      "For whoever scans this line at filing; shown next to each of its slots. Frozen at publish. Remarks are kept per language: this writes the remark in the language picked in the BOM page's remark column header. Other screens show the remark in the UI's language, and none where there is none in it.",
    addItem: "Add line",
    editItem: "Edit line",
    parent: "Parent",
    noParent: "— (top level)",
    quantityHint:
      "Usage per parent. The whole-machine figure is worked out by multiplying through the ancestors — do not enter it here.",
    criticalHint:
      "A critical line expands into one slot per whole-machine unit, each needing a serial at filing time. Non-critical lines are recorded as a quantity.",
    draftHint:
      "A draft is editable. {{critical}} critical lines so far, expanding to {{slots}} serials at filing. Publishing freezes everything but the remarks.",
    emptyDraft: "This draft has no lines yet. Add one, or go back and import a file.",
    deleteDraft: "Delete this draft",
    newDraftHint:
      "Creates an empty draft and opens it for editing. A draft has to be published before it can be used to create records.",
    emptyForType: "This device type has no BOM yet. Start a draft, or import a file.",
    publishedHint:
      "A published BOM is read-only except for its remarks, which can still be added and edited. To change anything else, derive a new version.",
    effectiveHint:
      "Per-machine quantity = this line's quantity × every ancestor's. Critical lines expand into that many slots, each needing one serial.",
    preflightTitle: "Import preflight",
    preflightPassed: "Preflight passed: {{rows}} lines, {{slots}} serials once expanded",
    preflightFailed: "Preflight found {{count}} problems",
    commitImport: "Import as a draft",
    issueRow: "Row",
    issueColumn: "Column",
    issueCode: "Issue",
    issueValue: "Value",
    issueSeq: "No.",
    issueLocation: "Install location",
    issueMaterialCode: "Part no.",
    issueDescription: "Description",
    importFormat: "Format",
    importFormats: { ENGINEERING: "Engineering BOM", TEMPLATE: "Position-code template (former)" },
    importSheet: "Sheet",
    rootMaterialCode: "Machine part no.",
    suggestedName: "Suggested name",
    warningsTitle: "{{count}} warnings (they do not block the import)",
    ignoredColumns: "Not imported: {{columns}}",
    acknowledgeWarnings: "I have checked these warnings",
    engineeringRemark: "Engineering remark",
    moduleType: "Module",
    moduleTypeOf: "Module type of {{line}}",
    notAModule: "Not a module",
    publishModules:
      "A robot filing scans {{scans}} codes per machine ({{modules}} modules); each module's scans are below.",
    modules: {
      allLines: "All modules",
      robotOnly: "Robot's own lines",
      module: "Module",
      assembly: "Assembly",
      lines: "Lines",
      scans: "Scans to file",
      robot: "Robot",
      serials: "{{n}} serials",
      moduleCodes: "{{n}} × {{module}} code",
    },
    suggestedModules: "Suggested modules (changeable on the draft after importing)",
  },
  archive: {
    copyLink: "Copy a link to this record",
    scrapConfirmButton: "Scrap this machine",
    create: "Create Device",
    moduleUpdated: "{{module}} module updated to v{{version}}:",
    causedBy: "Caused by a change to",
    versionCreated: "Filed",
    versionScrapped: "Scrapped",
    versionRecord: "Maintenance record of v{{version}}",
    openFullRecord: "Open the full page",
    addRobot: "New robot",
    addModule: "New module",
    kind: "Kind",
    kindRobot: "Robot",
    installedModuleReason:
      "A fitted module cannot be scrapped: take it out first, or scrap it with its robot",
    kindFilter: "Kind: {{kind}}",
    moduleState: "Module state",
    moduleInstalled: "Fitted",
    kindModule: "Module",
    createTitle: "Create Device",
    foundAt: "Found at",
    foundBefore: "Taken off · last in v{{version}}",
    searchTitle: "Device List",
    searchPlaceholder: "Archive no. / device ID / serial",
    device: "Device ID",
    archiveNo: "Archive number",
    productType: "Device type",
    status: "Status",
    statusActive: "Active",
    statusScrapped: "Scrapped",
    version: "Version",
    createdAt: "Filed at",
    current: "Current state",
    versions: "Version history",
    compare: "Compare versions",
    maintenance: "Maintenance",
    scrap: "Scrap",
    scrappedReason: "Already scrapped",
    position: "Material Info",
    materialCode: "Material",
    serial: "Serial",
    showRows: "Show",
    bomVersion: "BOM version",
    findPart: "Search position, serial, material or remark",
    filter: {
      all: "All {{n}}",
      critical: "Critical {{n}}",
      counted: "Non-critical {{n}}",
    },
    counted: "Counted ×{{n}}",
    quantity: "Quantity",
    addedByChange: "Added by a change",
    addedByChangeHint: "Added by a maintenance event; the BOM has no such position",
    removed: "Removed",
    partScrapped: "Scrapped",
    partScrappedHint: "This serial's part was recorded as scrapped when an archive was scrapped",
    removedHint: "Quantity 0: this part has been taken off the machine",
    noBom: "Filed without a BOM",
    bomFilter: "Filtered by BOM: {{version}}",
    searchFilter: "Search: {{q}}",
    productTypeFilter: "Device type: {{name}}",
    statusFilter: "Status: {{status}}",
    clearFilter: "Remove “{{label}}”",
    offBom: "Off BOM",
    offBomCount: "{{n}} off-BOM parts",
    progress: "{{done}} of {{total}} scanned — {{remaining}} to go",
    complete: "All slots filled",
    incompleteHint:
      "Every critical slot must be filled before this can be submitted. A machine that is genuinely short a part cannot be filed yet — finish the build, or have a process engineer derive a BOM version where that part is not critical.",
    noEntriesYet: "No entries yet. Add one for each part on this machine.",
    freeEntries: "Custom entries",
    addEntry: "Add entry",
    scanHint: "A scan lands in the next empty slot automatically.",
    scanPlaceholder: "Scan the serial for “{{remark}}”",
    locateFirstEmpty: "Jump to the first empty slot",
    draftRestored: "Restored your saved draft ({{time}})",
    draftDiscard: "Discard draft",
    draftNoDevice: "(no device ID yet)",
    deviceOptional: "Optional: can be filled in on the archive page later",
    noDeviceNo: "No device ID yet",
    fillDeviceNo: "Fill in device ID",
    editDeviceNo: "Edit device ID",
    snOptionalPlaceholder: "Optional",
    snOptionalHint:
      "Enter the SN printed on the robot or module label, if available. Leave blank to have the system generate one.",
    robotSnLabel: "Robot SN",
    snInvalid: "A robot SN has 18 characters, e.g. 162E1SHA2606300005",
    closeTitle: "Close Create Device?",
    closeKeepFilling: "Keep filling in",
    closeConfirm: "Close",
    closeBody: {
      saved: "What you entered is saved as a draft; continue it from Unfinished drafts.",
      nothing: "No serial numbers entered yet, so no draft will be kept.",
      failed:
        "The draft could not be saved (browser storage unavailable); closing loses what you entered.",
    },
    drafts: "Unfinished drafts ({{n}})",
    draftsEmpty: "No drafts yet. Add a device to start filing.",
    draftsHint:
      "Entries are saved as drafts automatically. Everyone who can create records sees them and can carry them on (one draft per device; the last save wins). Deleted once submitted.",
    draftEntered: "Entered",
    draftSavedTime: "Saved",
    draftSavedBy: "Last saved by",
    draftSearch: "Search drafts",
    draftSearchPlaceholder: "Device ID, SN, type, device type or who saved it",
    draftsShown: "Showing {{shown}} of {{total}}",
    draftsNoMatch: "No drafts match.",
    draftActions: "Actions",
    draftResume: "Resume",
    draftResumeOf: "Resume {{device}}",
    draftDeleteOf: "Delete the draft of {{device}}",
    draftDeleteTitle: "Delete draft?",
    draftDeleteBody:
      "The draft for {{device}} ({{n}} entered) will be deleted. This cannot be undone.",
    draftDiscardBody:
      "The {{n}} entries in this draft will be cleared and deleted. This cannot be undone.",
    draftSaved: "Saved as a draft ({{time}})",
    scrapConfirm:
      "Scrapping releases every serial on this machine so they can be registered elsewhere. This cannot be undone.",
    scrapReason: "Reason",
    scrapReleased: "Released {{count}} serials",
    chooseProductType:
      "Select a device type first. If a BOM is configured, enter component serial numbers for each BOM item by scanning or typing. Otherwise, add custom entries.",
    duplicateSerials: "The same serial appears twice on this machine: {{serials}}",
    installedTitle: "{{n}} serial(s) already installed on another machine:",
    installedAt:
      "{{serial}} is installed in “{{position}}” on device {{deviceNo}} ({{productType}}) since {{time}} — archive",
    installedShort: "Installed on {{archiveNo}} “{{position}}”",
    installedBlocksSubmit:
      "A serial is already installed on another machine. Use a part that is not installed elsewhere before submitting.",
    scan: {
      duplicate: "Serial {{serial}} is already in another slot on this machine",
      "ignored-repeat": "Ignored a repeated scan ({{serial}})",
      placeholder: "That is not a serial, so no slot was filled",
      "no-empty-slot": "Every slot is already filled",
      "format-mismatch":
        "{{serial}} does not match the barcode rules of “{{position}}”, material {{material}} ({{patterns}}), so it was not entered",
    },
  },
  scrap: {
    modulesTitle: "Where its modules go",
    fateOf: "Where {{archiveNo}} goes",
    release: "Back to stock",
    scrapWith: "Scrap with it",
    partsTitle: "Where the serialized parts go ({{n}})",
    partsHint:
      "Every part's fate is recorded. One restocked can be fitted to another machine; one scrapped is flagged as scrapped if it is entered again.",
    partFate: "Goes to",
    partFateOf: "Where {{position}} goes",
    restocked: "Back to stock",
    scrapped: "Scrapped",
    allRestocked: "All back to stock",
    allScrapped: "Scrap all",
    partsRecorded: "Parts recorded: {{scrapped}} scrapped, {{restocked}} back to stock.",
    staysInside: "Its {{n}} lower modules go to stock inside it",
  },
  scrappedSerial: {
    title: "This part was scrapped",
    body: "The part with serial {{serial}} was scrapped: it was on {{archiveNo}} “{{position}}” and recorded as scrapped when that archive was scrapped on {{date}}.",
    question: "A scrapped part should not go back on a machine. Enter it anyway?",
    remove: "Take this serial out",
    keep: "Enter it anyway",
  },
  moduleArchive: {
    location: "Where it is",
    inStock: "In stock",
    installations: "Installations",
    neverInstalled: "Never fitted in a robot or module yet",
    parent: "Fitted in",
    installedAt: "Fitted",
    removedAt: "Removed",
    parentVersions: "Parent versions",
    stillInstalled: "Still fitted",
    showContents: "Show the contents of {{archiveNo}}",
    hideContents: "Hide the contents of {{archiveNo}}",
  },
  moduleFiling: {
    title: "New module record",
    nestedTitle: "File on the spot: {{position}}",
    moduleType: "Module type",
    noModules: "This BOM has no modules marked",
    snLabel: "Module SN",
    filedOnSpot: "SN of module {{archiveNo}}, just filed",
    draftNoSn: "(SN given on submitting)",
    snInvalid:
      "A module SN has 14 characters with the letter of the chosen type “{{type}}” 4th, e.g. 150W2610230001",
    slotsTitle: "Modules",
    slotsProgress: "{{done}} / {{total}} fitted",
    code: "Module code",
    state: "Status",
    waiting: "Waiting for a scan",
    checking: "Looking up…",
    duplicate: "{{archiveNo}} is already in another module slot of this filing",
    notFound: "This module has no record yet",
    fileHere: "File it now",
    lookupFailed: "The lookup failed — try again",
    problem: {
      not_active: "This module is scrapped and cannot be fitted",
      type_mismatch: "This is a {{actual}} module; this slot takes {{expected}}",
      product_type_mismatch: "This module belongs to another product type and cannot be fitted",
      bom_mismatch:
        "This module was built to BOM {{theirs}}; at filing a module must share the BOM version",
      installed: "Fitted in {{where}} — cannot be fitted here",
      incomplete: "This module is missing lower modules ({{slots}}); complete it first",
    },
    scan: {
      "module-no-slot": "No empty {{module}} module slot to fit it into",
      "robot-sn": "That is a robot SN, not a module code or a part serial",
    },
  },
  maintenance: {
    serialBlocked: "A serial is installed elsewhere or entered twice; correct it to submit",
    formatBlocked: "A serial does not match its material's barcode rules; correct it to submit",
    moduleBlocked: "A module cannot go into the slot it was entered for; correct it to submit",
    moduleBomChanged: "Note: the new module is BOM {{after}}, the one it replaces {{before}}",
    moduleBomChangedSummary:
      "Note: {{n}} module(s) built to another BOM version than the one replaced",
    moduleBomNone: "(no version)",
    moduleFits: "Fits: {{archiveNo}}",
    moduleDuplicate: "{{archiveNo}} is entered for two module slots",
    jiraUrl: "Jira link",
    jiraUrlPlaceholder: "https://…/browse/SUP-123",
    jiraUrlHint: "Optional: paste the Jira ticket for this change; the record links to it",
    jiraUrlInvalid: "Enter a full link starting with http:// or https://, or leave it blank",
    jiraOpen: "Jira",
    pendingCorrection:
      "{{archive}}maintenance record #{{n}} can still be corrected (until {{time}}). Once this change is submitted it can no longer be edited or revoked; if it is wrong, correct it first.",
    pendingCorrectionOpen: "Open maintenance record #{{n}}",
    leaveTitle: "Discard this change?",
    leaveBody:
      "What you entered ({{n}} parts ticked or added) has not been submitted. A change is not kept as a draft, so leaving loses it.",
    leaveKeepEditing: "Keep editing",
    leaveDiscard: "Discard and leave",
    pick: "Select",
    filterSelected: "Selected {{n}}",
    newMaterialCode: "New material number",
    newMaterialName: "New material description",
    fittedModule: "Module fitted",
    newModulePlaceholder: "Scan the new module's SN or record number",
    thisArchive: "This record",
    archiveSummary: "{{archive}}: {{replaced}} replaced, {{removed}} removed",
    moduleConflict: "A module taken out cannot also have its parts changed in the same submission",
    affected: "New versions: {{list}}",
    fittedSerial: "New serial number",
    substituteNeedsBoth:
      "A substitute material needs both its new material number and description.",
    useSubstitute: "Use Substitute Material",
    substituteHint: "Only when the material fitted differs from the one the BOM specifies",
    substituteHintNoBom: "Only when the material changed",
    replaceQuantity: "Replacement quantity:",
    removeQuantity: "Removal quantity:",
    currentQuantity: "; current quantity: {{n}}",
    newSerialPlaceholder: "Scan or type",
    groupBadge: "Assembly · {{n}} parts",
    groupHint:
      "This row is itself a replaceable component; ticking it records a change to it alone, not to the {{n}} parts under it",
    pickedBelow: "{{n}} selected",
    groupNote:
      "Records a change to “{{name}}” itself only; the {{n}} parts under it are not changed with it. Tick any of them that were replaced as well.",
    selectedSummary: "{{n}} selected: {{replaced}} replaced, {{removed}} removed, {{added}} added",
    submit: "Submit Changes",
    stepReason: "1. Change Type and Description",
    stepParts: "2. Select Components",
    partsStepPick: "Tick each component to change, then fill in its details.",
    partsStepFill:
      "Replace: scan or enter the new serial number of a critical component, or the replacement quantity of a non-critical one. Remove: no new component details are required.",
    partsStepAdd: "For a component not listed in the BOM, use “Add Non-BOM Component” above.",
    partsStepAddNoBom: "For a new component, use “Add Component” above.",
    findPart: "Search by position, material number, serial number, or remark",
    shownParts: "Showing {{n}} of {{total}} items",
    currentlyFitted: "Current Component",
    addOutOfBom: "Add Non-BOM Component",
    addOutOfBomNoBom: "Add Component",
    replaceNeedsSerial:
      "A replacement needs the serial that was fitted, or it records nothing about what changed.",
    none: "Nothing has been changed on this machine yet.",
    actions: "Actions",
    open: "Open",
    correct: "Edit",
    revoke: "Revoke",
    correctableFor: "Correctable until {{time}} ({{minutes}} min left)",
    correctableHint:
      "For two hours after it is submitted, and while nothing has been recorded after it, this record can be edited or revoked; after that it is final and a mistake needs a new record",
    correctTitle: "Edit maintenance record #{{n}}",
    saveCorrection: "Save changes",
    correctUnsupported:
      "This record holds a change that cannot be edited here; revoke it and record it again",
    revokeTitle: "Revoke maintenance record #{{n}}?",
    revokeBody:
      "The devices it changed go back to how they were before it, and the record leaves the list (the audit log keeps it). Change: {{reason}}",
    openRecord: "Open maintenance record #{{n}}",
    copyLink: "Copy a link to this maintenance record",
    backToList: "← All maintenance records",
    eventTitle: "Maintenance record #{{n}}",
    title: "Maintenance",
    start: "Record Change",
    action: "Action",
    replace: "Replace",
    add: "Add",
    remove: "Remove",
    oldSerial: "Removed serial",
    newMaterial: "New material (if different from BOM)",
    newMaterialNoBom: "New material (if changed)",
    newSerial: "Fitted serial",
    newSerialOrCount: "New Serial Number / Quantity",
    reason: "Description",
    reasonPlaceholder: "Optional; required when the type is Other",
    changeType: "Change Type",
    changeTypeChoose: "Choose…",
    changeColumn: "Change",
    changeTypes: {
      REPAIR: "Repair",
      PREVENTIVE: "Preventive maintenance",
      UPGRADE: "Upgrade",
      ASSEMBLY: "Assembly adjustment",
      QUALITY: "Quality issue",
      OTHER: "Other",
    },
    occurredAt: "Occurred at",
    occurredAtHint: "Left as is, the time of submission is recorded",
    occurredBeforeFiling: "Cannot be earlier than the record was created, {{time}}",
    occurredInFuture: "Cannot be later than now ({{time}})",
    occurredBeforeModule:
      "The time is before {{archive}} was filed ({{time}}), and this change reaches into it: go back to step 1 and change the time, or leave it blank",
    occurredBeforeLast:
      "Earlier than the last change ({{time}}): saved as a late record, listed first in the history",
    newPosition: "New out-of-BOM position",
    newPositionNoBom: "New position",
    addLine: "Add line",
    incompleteLines:
      "Line {{lines}} is not finished: replace and remove need a position, replace also needs the fitted serial, and add needs either a position or a new position name.",
    sequence: "Seq",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    submittedAt: "Submitted",
    submittedBy: "Submitted by",
    unchangedBadge: "Quantity only",
    scrapBadge: "Scrapped",
    identicalHint:
      "Both versions hold exactly the same assembly — the change is in the events below.",
    filterPosition: "Filter by position",
    filterAction: "Filter by action",
  },
  table: {
    resizeColumn: "Resize column {{n}}",
    resizeHint: "Drag to resize; double-click to reset",
  },
  tree: {
    levels: "Levels",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    expand: "Expand {{name}}",
    collapse: "Collapse {{name}}",
    disabledWhileFiltered: "While filtering or searching every match is shown; folding is off",
    slots: "{{n}} slots",
  },
  serialStatus: {
    checking: "Checking…",
    free: "Not in use — can be fitted",
    usedRemoved: "Was on {{archiveNo}} “{{position}}”, taken off (up to v{{version}})",
    usedScrapped: "Was on {{archiveNo}} “{{position}}”, scrapped with it",
    usedPartScrapped: "Was on {{archiveNo}} “{{position}}”; the part was scrapped with it",
    usedPartRestocked:
      "Was on {{archiveNo}} “{{position}}”; taken off and restocked when it was scrapped",
    swap: "Taken off “{{position}}” in this change — a swap",
    duplicate: "Already entered elsewhere in this change",
    formatMismatch: "Does not match the barcode rules of material {{material}}: {{patterns}}",
  },
  robotSn: {
    print: "Print label",
    printProdOnly:
      "Labels print in production only: an SN generated in a test environment may repeat a real one",
    testMark: "TEST",
    testOnly: "Generated in a test environment — for testing only, not for real devices",
    label: "SN",
    qrLabel: "QR code of SN {{sn}}",
    barcodeLabel: "Barcode of SN {{sn}}",
    copy: "Copy SN",
    typeCode: "SN Type Code",
    typeCodeHint:
      "Characters 4–5 of the SN: two capitals or digits. Defaults to the archive prefix's first letter + 1 (moved on if taken); cannot be changed after creation",
  },
  position: {
    replace: "Replace this part",
    swap: "Swap with another slot",
    scrapped: "A scrapped archive cannot be changed",
    change: "Change this part",
    nothingFitted: "All removed: nothing left to change",
    swapWith: "Swap with",
    swapPreview:
      "After this, “{{here}}” holds {{thereSerial}} and “{{there}}” holds {{hereSerial}}, recorded as one change.",
    useSwap: "Swap with “{{position}}” on this machine instead",
    history: "Change history",
    filed: "Filed",
    noHistory: "Nothing recorded yet.",
    open: "Open the history of {{position}}",
    currentSerial: "Current Serial Number",
    materialCode: "Material Code",
    copySerial: "Copy serial number",
    copyMaterialCode: "Copy material code",
    newSerial: "New Component Serial Number",
    newSerialPlaceholder: "Scan or enter serial number",
    material: "Material",
    useSubstitute: "Use Substitute Material",
    keepMaterial: "Keeps material code: {{code}}",
    reason: "Replacement Description",
    occurredAt: "Replacement Time",
    occurredAtHint: "If left blank, the submission time will be used as the replacement time.",
    save: "Save",
    missing: "Not saved yet. Please fill in: {{fields}}",
    listSeparator: ", ",
    saved: "Replacement record saved (version v{{version}}).",
    action: "Change",
    quantity: "Quantity",
    quantityHint: "Currently fitted: {{n}}",
    currentQuantity: "Current Quantity",
    changeReason: "Change Description",
    changeOccurredAt: "Change Time",
    changeOccurredAtHint: "If left blank, the submission time will be used as the change time.",
    changeSave: "Save",
    changeSaved: "Change record saved (version v{{version}}).",
  },
  datetime: {
    timeZone: "Time zone: {{zone}} ({{offset}})",
    placeholder: "YYYY-MM-DD HH:mm, e.g. 2026-10-13 14:30",
    open: "Pick a date and time",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    hour: "Hour",
    minute: "Minute",
    now: "Now",
    clear: "Clear",
    done: "Done",
    invalid: "Not a valid date and time. Use the form 2026-10-13 14:30",
  },
  compare: {
    fromVersion: "From version",
    toVersion: "To version",
    modules: "Modules",
    moduleSlot: "Module slot",
    moduleSwapped: "Module swapped",
    moduleUpdated: "Changed inside",
    moduleBomChanged: "Different BOM version",
    pinnedVersion: "Archive v{{version}}",
    bomVersion: "BOM {{version}}",
    noBomVersion: "(no version)",
    moduleBomSummary: "Module of another BOM version in {{count}} slot(s): {{positions}}",
    emptySlot: "(empty)",
    added: "Added",
    removed: "Removed",
    changed: "Changed",
    before: "Before",
    after: "After",
    newMaterial: "New material",
    newMaterialSummary: "New material in {{count}} position(s): {{positions}}",
  },
  system: {
    title: "System",
    language: "Interface language",
    languageSwitch: "Chinese interface",
    account: "Signed in as",
    sessionExpires: "Session expires",
    sessionHint: "Sessions expire 30 days after sign-in and are never extended by use.",
    clientVersion: "Client version",
    minVersion: "Minimum version required by the server",
    serverTime: "Server time",
  },
  audit: {
    title: "Audit Log",
    actor: "Actor",
    actorEmail: "Actor email",
    action: "Action",
    entity: "Entity",
    entityId: "Entity ID",
    occurredAt: "Time",
    allEntityTypes: "All entity types",
    details: "Details",
    requestId: "Request ID",
    before: "Before",
    after: "After",
    entityTypes: {
      device_archive: "Device archive",
      maintenance_event: "Maintenance",
      bom: "BOM",
      product_type: "Device type",
      robot_sn: "Robot SN",
      bom_import_batch: "BOM import",
      archive_import_batch: "Archive import",
      app_user: "User",
      user_session: "Sign-in session",
    },
    actions: {
      auth_login_succeeded: "Signed in",
      auth_login_rejected: "Sign-in rejected",
      auth_logout: "Signed out",
      bom_import_preflight: "BOM import checked",
      bom_import_committed: "BOM import committed",
      archive_import_preflight: "Archive import checked",
      archive_import_committed: "Archive import committed",
      archive_created: "Archive filed",
      archive_batch_created: "Archives filed in bulk",
      archive_scrapped: "Scrapped",
      maintenance_submitted: "Maintenance submitted",
      product_type_created: "Type created",
      product_type_discontinued: "Type discontinued",
      product_type_activated: "Type activated",
      product_type_deleted: "Type deleted",
      product_type_sn_type_code_set: "SN type code set",
      bom_created: "BOM created",
      bom_published: "BOM published",
      bom_derived: "BOM derived",
      bom_deleted: "BOM deleted",
      bom_disabled: "BOM disabled",
      bom_enabled: "BOM enabled",
      bom_item_changed: "BOM line changed",
      bom_critical_marked: "Critical part marked",
      robot_sn_reserved: "SN reserved",
    },
  },
  conflict: {
    title: "This archive changed while you were working",
    body: "You opened version {{base}}; it is now version {{latest}}. Your entries are kept, but they cannot be submitted as they are — look at the current state and decide whether your change still applies.",
    openLatest: "Open the latest version in a new tab",
    refresh: "Refresh and confirm again",
  },
  error: {
    field: {
      string_pattern_mismatch: "does not match the required format",
      string_too_short: "is too short",
      string_too_long: "is too long",
      missing: "is required",
      uuid_parsing: "is not a valid identifier",
      int_parsing: "must be a whole number",
      greater_than_equal: "is below the minimum",
      datetime_parsing: "is not a valid time",
      bool_parsing: "must be yes or no",
    },
    title: "Something went wrong",
    requestId: "Request id: {{id}}",
    clockSkew: "This device: {{client}}. Server: {{server}}. About {{minutes}} min apart.",
    timeBeforeFiling:
      "The time {{occurred}} is before {{archive}} was filed, {{created}}. Use a later time, or leave it blank to record the time of submission.",
    timeInFuture:
      "The time {{occurred}} is about {{minutes}} min ahead of the server's clock ({{server}}). Check this computer's clock, or leave the time blank.",
    serialFormat:
      "{{position}}: {{serial}} does not match the barcode rules of material {{material}} ({{patterns}})",
    network: "The request failed — check the connection and retry",
  },
} as const;
